import draftToHtml from './draftjsToHtml/js';
import {reportConversionFailure} from './reportConversionFailure';

export type DraftEntity = {type: string; mutability?: string; data: Record<string, unknown>};

export type CustomEntityTransform = (entity: DraftEntity, text: string) => string | undefined;

/**
 * Whether this content is a DraftJS document, and its parsed form if it is.
 *
 * Three things arrive here and only one of them is a document: stored DraftJS,
 * Jira-synced HTML, and the occasional piece of JSON that is not a document at all.
 * Separating them is what lets a genuine converter failure be reported without
 * burying it under one event per task open for every customer with synced content.
 */
const parseDraftJsDocument = (content: string): {blocks: unknown[]} | null => {
	let parsed: unknown;
	try {
		parsed = JSON.parse(content);
	} catch {
		// Not JSON, so it is html or plain text. Expected, and returned untouched.
		return null;
	}

	const document = parsed as {blocks?: unknown};
	return Array.isArray(document?.blocks) ? (document as {blocks: unknown[]}) : null;
};

/**
 * `report` exists for the drift detector, which round-trips stored content the user
 * never opened an editor on. A throw there is diagnostic, not a rendering failure the
 * user saw, and reporting it would put a phantom event next to the real ones.
 */
export const convertFromDraftJsToHtml = (
	draftJsOrHtml: string,
	customEntityTransform?: CustomEntityTransform,
	{report = true}: {report?: boolean} = {}
): string => {
	if (!draftJsOrHtml) {
		return '';
	}

	const document = parseDraftJsDocument(draftJsOrHtml);
	if (!document || document.blocks.length === 0) {
		return draftJsOrHtml;
	}

	try {
		return draftToHtml(document, undefined, undefined, customEntityTransform);
	} catch (e) {
		// A genuine defect, unlike the two passthrough cases above: this content IS a
		// DraftJS document and we could not render it. Previously indistinguishable from
		// them, so it showed the user raw JSON where their description should be and
		// told nobody.
		if (report) {
			reportConversionFailure('draftjs-to-html', e, draftJsOrHtml);
		}

		// Still the stored value rather than ''. Raw JSON in the editor is bad; an empty
		// description where the customer wrote one is worse, and reads as data loss.
		return draftJsOrHtml;
	}
};
